import { useCallback, useEffect, useMemo, useState } from "react";
import { createPayrollRun, fetchPayrollHub } from "../../Moduels/payroll/payrollApi";
import { isDisbursementEligibleRun, resolveRunStatus } from "../../Moduels/payroll/payrollRunUtils";
import SearchableSelect from "./SearchableSelect";

export function formatPayrollRunLabel(run, period) {
  const periodLabel =
    period?.period_label ||
    run?.period_label ||
    (period?.start_date && period?.end_date
      ? `${period.start_date} → ${period.end_date}`
      : "Period");
  const status = resolveRunStatus(run);
  return `${periodLabel} (${status.label})`;
}

function buildOptionsFromHub(periods = [], { disbursementOnly = false } = {}) {
  const opts = [];
  for (const period of periods) {
    for (const run of period.runs || []) {
      if (disbursementOnly && !isDisbursementEligibleRun(run)) continue;
      const status = resolveRunStatus(run);
      const periodLabel =
        period.period_label ||
        (period.start_date && period.end_date
          ? `${period.start_date} → ${period.end_date}`
          : "Period");
      opts.push({
        value: run.payroll_run_id,
        label: periodLabel,
        sublabel: status.label,
        group: period.start_date ? String(period.start_date).slice(0, 4) : "Other",
        badge: { label: status.label, className: status.className },
        statusKey: status.key,
        id: run.payroll_run_id,
        pay_period_id: period.pay_period_id,
        run,
        period,
        sortKey: period.start_date || run.period_start || "",
      });
    }
  }
  opts.sort((a, b) => String(b.sortKey).localeCompare(String(a.sortKey)));
  return opts;
}

async function ensureDraftRunsForOrphanPeriods(periods = []) {
  const orphans = periods.filter((p) => !(p.runs || []).length);
  if (!orphans.length) return null;

  await Promise.all(
    orphans.map((period) =>
      createPayrollRun({
        organisation_id: period.organisation_id,
        pay_period_id: period.pay_period_id,
      }).catch(() => null)
    )
  );
  const res = await fetchPayrollHub();
  return res.data?.periods || [];
}

export default function PayrollRunSelect({
  value,
  onChange,
  label = "Payroll run",
  required = false,
  className = "",
  disabled = false,
  allowEmpty = true,
  emptyLabel = "Select payroll run",
  hint = "",
  disbursementOnly = false,
  statusFilter = "",
  reloadToken = 0,
  liveStatus = null,
}) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orphanCount, setOrphanCount] = useState(0);

  const loadOptions = useCallback(async () => {
    setLoading(true);
    try {
      let periods = (await fetchPayrollHub()).data?.periods || [];
      const orphans = periods.filter((p) => !(p.runs || []).length);
      setOrphanCount(orphans.length);

      if (orphans.length > 0 && !disbursementOnly) {
        const synced = await ensureDraftRunsForOrphanPeriods(periods);
        if (synced) {
          periods = synced;
          setOrphanCount(0);
        }
      }

      setOptions(buildOptionsFromHub(periods, { disbursementOnly }));
    } catch {
      setOptions([]);
      setOrphanCount(0);
    } finally {
      setLoading(false);
    }
  }, [disbursementOnly]);

  useEffect(() => {
    loadOptions();
  }, [loadOptions, reloadToken]);

  const selectOptions = useMemo(
    () =>
      options
        // Optional run-status filter (e.g. Reports > Payroll Status).
        .filter((o) => !statusFilter || o.statusKey === statusFilter)
        .map((o) => {
          const useLive = liveStatus && String(o.value) === String(value);
          return {
            value: o.value,
            label: o.label,
            sublabel: useLive ? liveStatus.label : o.sublabel,
            group: o.group,
            badge: useLive
              ? { label: liveStatus.label, className: liveStatus.className }
              : o.badge,
            _raw: o,
          };
        }),
    [options, statusFilter, liveStatus, value]
  );

  if (!loading && options.length === 0) {
    return (
      <div className={className}>
        {label ? (
          <label className="hr-search-select-label">
            {label}
            {required ? " *" : ""}
          </label>
        ) : null}
        <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl p-3 space-y-2">
          <p>
            {disbursementOnly
              ? "No processed payroll runs found. Process payroll first, then create a salary batch."
              : "No payroll runs found. Create a pay period first — a draft run is created automatically."}
          </p>
          <button type="button" className="text-indigo-700 underline font-medium" onClick={() => loadOptions()}>
            Refresh list
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <SearchableSelect
        label={label}
        required={required}
        disabled={disabled}
        loading={loading}
        allowEmpty={allowEmpty}
        emptyLabel={emptyLabel}
        searchPlaceholder="Search by period, status, or year…"
        hint={hint}
        value={value}
        options={selectOptions}
        onChange={(id, opt) => onChange(id, opt?._raw || null)}
        noResultsText={
          statusFilter && !selectOptions.length
            ? "No payroll runs with the selected status"
            : "No payroll runs match your search"
        }
      />
      {orphanCount > 0 && !loading && !disbursementOnly ? (
        <p className="mt-1 text-xs text-amber-700">
          {orphanCount} pay period{orphanCount === 1 ? "" : "s"} had no payroll run — creating draft run
          {orphanCount === 1 ? "" : "s"}…
        </p>
      ) : null}
    </div>
  );
}
